// THE RUN DESK ('#run?id=N') — the one place a run is watched while it works
// (BUILD-BRIEF-R3f §2).
//
// A run used to be a row on the Agents page and a `RUN-` ref in a summary: what it was doing
// right now was invisible until it finished, and an operator who cannot watch a run learns not
// to trust it ("71k tokens and two cells"). This page answers four questions, in this order:
//
//   STEPS       what it is doing, as it does it — the conversation, appended live at a three
//               second cadence (P7: polling, never a socket). `drop` and `refusal` rows are the
//               only coloured ones, because they are the only rows that say something did not
//               happen.
//   PROPOSALS   what it filed. ◇ until an operator holds it, ▪ after. P1 lives in Review, and
//               every row here is a link into it.
//   LEDGER      every tool call it made, refusals included — the row the registry wrote.
//   REPORT      the harness's final answer once the run has written it; while it runs, the
//               child's own stderr tail, labelled as exactly that and nothing more.
//
// WHAT THIS PAGE MUST SURVIVE: a server that has no steps route yet. A 404 or a 501 on
// `/runs/:id/steps` means the server has recorded no steps — a state of the record, not an
// error — so the zone says so in words, the queued rail cell shows no position, nothing is
// logged to the console, and the poll of the run itself carries on.
//
// ROUND 4 TOOK THE RECEIPT AWAY (BUILD-BRIEF-R4 §4). No run writes one any more: the Steps zone
// and the proposals in Review ARE the account. The Cells rail cell counts this run's own `fact`
// proposals — a computed count, so it carries no claim mark.
//
// NOTHING HERE IS DERIVED THAT THE SERVER DID NOT SAY. The tokens, the counts and the gate's
// position all arrive already decided; the elapsed clock is the one exception, and it is
// arithmetic over `started_at`, which carries no claim mark.
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import ConfirmModal from '../components/ConfirmModal.js';
import EmptyState from '../components/EmptyState.js';
import { swissTicks } from '../components/DocumentsZone.js';
import {
  Att, Fig, Lnk, Mast, PageShell, Rail, RailCell, Ref, Sep, Sheet, Tag, Zone, ZoneHead,
} from '../components/PageKit.js';
import { useToast } from '../components/Toast.js';
import { useLocale, useT } from '../i18n/index.js';
import { href, open as openRoute, ROUTES, scrollToAnchor } from '../routes.js';
import { abortRun, getRun, getRunSteps, stepsUnavailable, type RunDetailView } from '../api/runs.js';
import { ApiError } from '../api/client.js';
import { tCtx } from '../../shared/i18n.js';
import type { RunStep, RunStepKind, RunStatus, ToolOutcome } from '../../shared/types.js';
import type { PageProps } from './registry.js';
import './RunPage.de.js';
import './RunPage.fr.js';

/** The live cadence. Three seconds while the run is queued or running, then one last pass. */
const POLL_MS = 3_000;
/** The route's page size for steps (`limit` ≤ 500): a page this long is not the last one. */
const STEPS_PAGE = 500;

/** The closed set of step kinds, each with the short word the Tag shows (R3f §1). */
export const STEP_LABEL: Record<RunStepKind, string> = {
  queued: 'queued',
  started: 'started',
  plan: 'plan',
  search: 'search',
  fetch: 'fetch',
  read: 'read',
  model: 'model',
  propose: 'propose',
  drop: 'drop',
  refusal: 'refused',
  wait: 'waiting',
  finish: 'finish',
  truncated: 'truncated',
};

/** The one skill that fills raster cells — the run whose Cells figure is a count and not a
 *  "not collected". Every other skill files no `fact` proposal at all. */
const CELL_SKILL = 'deep_research';

/** The two kinds that say something did NOT happen. Nothing else on the list is coloured. */
export const BROKEN_KINDS = new Set<RunStepKind>(['drop', 'refusal']);

/** The ledger's three outcomes, as words. 'ok' is not a word an operator reads. */
const OUTCOME_WORD: Record<ToolOutcome, string> = { ok: 'answered', refused: 'refused', error: 'error' };

/** `2m 14s` — the same reading of a duration the Agents page gives a finished run. */
export function wallText(ms: number | null): string {
  if (ms === null || !Number.isFinite(ms) || ms < 0) return '—';
  const total = Math.round(ms / 1000);
  return total < 60 ? `${total}s` : `${Math.floor(total / 60)}m ${String(total % 60).padStart(2, '0')}s`;
}

/** A step's own clock, to the second: the conversation is read in order, not by date. */
export function stepTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

/**
 * The prose a `model` step carries, when it carries any. A model's own words are the record's
 * voice and are rendered as such; everything else in a `detail` is structure, and structure
 * reads as mono. Returning null is the honest answer for a step whose detail is neither.
 */
export function modelText(step: RunStep): string | null {
  if (step.kind !== 'model' || !step.detail) return null;
  for (const key of ['text', 'content', 'message', 'reasoning', 'output']) {
    const value = (step.detail as Record<string, unknown>)[key];
    if (typeof value === 'string' && value.trim()) return value;
  }
  return null;
}

/** What the rail's Status cell says, and in which tone. */
export function statusTone(status: RunStatus): 'ok' | 'attend' | 'broken' | 'nil' {
  if (status === 'running') return 'attend';
  if (status === 'done') return 'ok';
  if (status === 'failed' || status === 'aborted') return 'broken';
  return 'nil';
}

const ended = (status: RunStatus): boolean => status !== 'queued' && status !== 'running';

export default function RunPage({ runId }: PageProps) {
  const t = useT();
  const { fmtDateTime, fmtNum } = useLocale();
  const toast = useToast();
  const id = runId ?? 0;
  // A token count is a QUANTITY, and a five-figure quantity is unreadable ungrouped: 66’101, the
  // same grouping (and the same typographic apostrophe) every figure on this desk prints.
  const fig = (n: number): string => swissTicks(fmtNum(n));

  const [detail, setDetail] = useState<RunDetailView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [steps, setSteps] = useState<RunStep[]>([]);
  const [stepsTotal, setStepsTotal] = useState<number | null>(null);
  // null = nobody has asked yet · false = this server has no steps route · true = it answered.
  const [stepsRecorded, setStepsRecorded] = useState<boolean | null>(null);
  const [stepsError, setStepsError] = useState<string | null>(null);
  const [live, setLive] = useState(true);
  const [follow, setFollow] = useState(false);
  const [openStep, setOpenStep] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [abortOpen, setAbortOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const cursor = useRef(0);
  const tail = useRef<HTMLDivElement | null>(null);
  const followDecided = useRef(false);

  /**
   * ONE LOOP, TWO READS. The run row and the steps past the cursor are fetched together so the
   * status and the conversation can never disagree by a tick, and the next pass is scheduled
   * only while there is something left to see: three seconds while the run is queued or
   * running, one further pass after it ends, then nothing. No socket, no reconnect, no cleanup
   * anybody has to remember (P7).
   */
  useEffect(() => {
    if (!id) { setLoading(false); return; }
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let finalPassDone = false;

    const pass = async (): Promise<void> => {
      let over = false;
      let full = false;
      try {
        const next = await getRun(id);
        if (!alive) return;
        setDetail(next);
        setError(null);
        over = ended(next.run.status);
      } catch (err) {
        if (!alive) return;
        // The law of usePoll, kept by hand: a value is never blanked because a refresh failed.
        setError(err instanceof Error ? err.message : String(err));
        over = false;
      } finally {
        if (alive) setLoading(false);
      }

      try {
        const page = await getRunSteps(id, cursor.current);
        if (!alive) return;
        setStepsRecorded(true);
        setStepsError(null);
        setStepsTotal(page.total);
        if (page.items.length) {
          cursor.current = page.last_id;
          setSteps((prev) => [...prev, ...page.items]);
        }
        // A full page means more is waiting: drain it now, do not wait a poll interval.
        full = page.items.length >= STEPS_PAGE;
      } catch (err) {
        if (!alive) return;
        // A server without the route has recorded nothing. That is not a failure to report.
        if (stepsUnavailable(err)) { setStepsRecorded(false); setStepsError(null); }
        else setStepsError(err instanceof Error ? err.message : String(err));
      }

      if (!alive) return;
      if (full) { timer = setTimeout(() => { void pass(); }, 0); return; }
      if (!over) { timer = setTimeout(() => { void pass(); }, POLL_MS); return; }
      if (!finalPassDone) { finalPassDone = true; timer = setTimeout(() => { void pass(); }, POLL_MS); return; }
      setLive(false);
    };

    void pass();
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [id]);

  /** The elapsed clock, and nothing else, ticks. It stops with the run. */
  useEffect(() => {
    if (!live) return;
    const tick = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(tick);
  }, [live]);

  /**
   * Follow is on for a run that is still working and off for one that is not — decided ONCE,
   * from the first answer, so a run that ends under the operator's eyes does not silently take
   * the toggle back off, and one opened after the fact does not scroll the page on arrival.
   */
  useEffect(() => {
    if (!detail || followDecided.current) return;
    followDecided.current = true;
    setFollow(!ended(detail.run.status));
  }, [detail]);

  useEffect(() => {
    if (!follow || !steps.length) return;
    tail.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [follow, steps.length]);

  const run = detail?.run ?? null;
  const calls = detail?.calls ?? [];
  const proposals = detail?.proposals ?? [];
  const gate = detail?.gate ?? null;
  const childLog = detail?.child_log ?? null;
  const refused = useMemo(() => calls.filter((c) => c.outcome === 'refused').length, [calls]);
  // An ERROR is not a REFUSAL. One is the registry saying no on purpose, the other is a call that
  // broke; a cell that folded them together would let a refusal hide behind a timeout.
  const errored = useMemo(() => calls.filter((c) => c.outcome === 'error').length, [calls]);

  const doAbort = useCallback(async () => {
    if (!id) return;
    setBusy(true);
    try {
      await abortRun(id);
      setAbortOpen(false);
      toast('ok', t('{ref} was asked to stop. It stops at its next step boundary.', { ref: run?.ref ?? `#${id}` }));
    } catch (err) {
      toast('err', err instanceof ApiError ? err.message : t('Could not stop this run.'));
    } finally {
      setBusy(false);
    }
  }, [id, run?.ref, toast, t]);

  // ── the two honest nothings ──

  if (!runId) {
    return (
      <PageShell>
        <Mast title={t('Run')} sub={t('no run was addressed')} />
        <EmptyState
          kind="error"
          title={t('This address names no run')}
          detail={t('A run desk is opened as #run?id=N from a RUN- ref — on the Agents panel, on an organisation’s brief, or beside a proposal in Review.')}
        />
      </PageShell>
    );
  }

  if (!run) {
    return (
      <PageShell>
        <a className="back" href={href(ROUTES.agents())}>← {t('Agents')}</a>
        <Mast title={t('Run')} sub={loading ? t('reading the run…') : t('the runtime did not answer')} />
        {error && !loading ? (
          <EmptyState
            kind="error"
            title={t('Could not read this run')}
            detail={<>GET /api/runs/{runId} — {error}</>}
          />
        ) : (
          <Sheet pad><p className="zone-empty">{t('Loading…')}</p></Sheet>
        )}
      </PageShell>
    );
  }

  // ── what kind of run this is ──
  //
  // The run's own words first: the `started` step names what the runner was asked for, and the
  // skill on the row is what it falls back to. Nothing is guessed.
  const startedWord = (steps.find((s) => s.kind === 'started')?.title ?? '').split('·')[0].trim();
  const kindWord = startedWord || run.skill;
  // CELLS. Only a research run fills the raster, so only a research run has a Cells figure; for
  // any other skill nobody counted cells and the figure reads "not collected", never zero. The
  // count itself is arithmetic over this run's own `fact` proposals — no claim mark (P12).
  const filesCells = run.skill === CELL_SKILL;
  const cellsFiled = proposals.filter((p) => p.kind === 'fact').length;

  const startedMs = run.started_at ? Date.parse(run.started_at) : NaN;
  const elapsedMs = run.status === 'running' && Number.isFinite(startedMs) ? now - startedMs : null;
  const tokensIn = run.tokens_in;
  const tokensOut = run.tokens_out;
  const tokensKnown = tokensIn !== null || tokensOut !== null;

  const statusUnit: ReactNode =
    run.status === 'running' ? wallText(elapsedMs)
      : run.status === 'queued' ? (gate?.position ? t('behind {n}', { n: gate.position }) : undefined)
        : wallText(run.wall_ms);

  const statusDetail: ReactNode =
    run.status === 'queued'
      ? (gate
        ? <>{t('{live} of {cap} running', { live: gate.live, cap: gate.cap_now })}{gate.night ? <> <Sep /> {t(gate.weekend ? 'weekend cap' : 'night cap')}</> : null}</>
        : t('the gate has not reported a position'))
      : run.error
        ? run.error
        : run.started_at
          ? t('started {when}', { when: fmtDateTime(run.started_at) })
          : t('not started yet');

  return (
    <PageShell>
      <a className="back" href={href(ROUTES.agents())}>← {t('Agents')}</a>

      <Mast
        kicker={
          <>
            {t('Run')} ·{' '}
            {/* the kicker is a .u-label, which uppercases and tracks; a ref is neither */}
            <span style={{ letterSpacing: 0, textTransform: 'none' }}><Ref>{run.ref}</Ref></span>
            {' '}· {kindWord}
          </>
        }
        title={
          run.subject_label && run.subject_kind && run.subject_id
            ? (
              <Lnk href={href(run.subject_kind === 'person'
                ? ROUTES.person({ id: run.subject_id })
                : ROUTES.org({ id: run.subject_id }))}
              >
                {run.subject_label}
              </Lnk>
            )
            : run.skill
        }
        sub={
          <>
            {run.model ? <Ref>{run.model}</Ref> : <span className="fv--unknown">{t('not collected')}</span>}
            <Sep />
            <Ref>{`run:${run.id}`}</Ref>
            <Sep />
            {t(run.triggered_by)}
            <Sep />
            {run.started_at
              ? <>{t('started')} <Fig>{fmtDateTime(run.started_at)}</Fig></>
              : <>{t('queued')} <Fig>{fmtDateTime(run.created_at)}</Fig></>}
            {run.ended_at && <><Sep />{t('ended')} <Fig>{fmtDateTime(run.ended_at)}</Fig></>}
            {run.subject_ref && <><Sep /><Ref>{run.subject_ref}</Ref></>}
            {error && <><Sep /><span className="fv--attend">{error}</span></>}
          </>
        }
        verbs={
          <>
            <button type="button" className="btn" onClick={() => openRoute(ROUTES.review({ principal: `run:${run.id}` }))}>
              {t('Open in Review')}
            </button>
            {!ended(run.status) && (
              <button type="button" className="btn btn-accent" onClick={() => setAbortOpen(true)}>{t('Abort')}</button>
            )}
          </>
        }
      />

      <Rail>
        <RailCell
          label={t('Status')}
          value={run.status === 'failed' ? tCtx(t, 'failed', 'run') : t(run.status)}
          unit={statusUnit}
          tone={statusTone(run.status)}
          detail={statusDetail}
        />
        {/* Tokens are the run's cost. Arithmetic — no claim mark, ever. */}
        <RailCell
          label={t('Tokens')}
          value={tokensKnown
            ? <><Fig>{fig(tokensIn ?? 0)}</Fig> / <Fig>{fig(tokensOut ?? 0)}</Fig></>
            : t('not collected')}
          tone={!tokensKnown ? 'unknown' : (tokensIn ?? 0) + (tokensOut ?? 0) === 0 ? 'nil' : 'ink'}
          detail={tokensKnown
            ? t('in / out')
            : run.status === 'queued' ? t('nothing has been spent yet')
              : run.status === 'running' ? t('the total arrives when the run ends · the steps count as they go')
                : t('the run reported no token count')}
        />
        <RailCell
          label={t('Tool calls')}
          go="→"
          value={<Fig>{calls.length}</Fig>}
          tone={calls.length === 0 ? 'nil' : 'ink'}
          detail={refused > 0 || errored > 0
            ? (
              <span className="fv--broken">
                {refused > 0 && t('{n} refused', { n: refused })}
                {refused > 0 && errored > 0 && ' · '}
                {errored > 0 && t('{n} failed', { n: errored })}
              </span>
            )
            : calls.length === 0 ? t('it has called nothing') : t('every call was answered')}
          onClick={() => scrollToAnchor('ledger')}
        />
        <RailCell
          label={t('Proposals filed')}
          go="→"
          value={<Fig>{proposals.length}</Fig>}
          tone={proposals.length === 0 ? 'nil' : 'ink'}
          detail={proposals.length === 0
            ? t('it has proposed nothing')
            : t('{unheld} unheld · {held} held · {refused} refused', {
              unheld: proposals.filter((p) => p.status === 'unheld').length,
              held: proposals.filter((p) => p.status === 'held').length,
              refused: proposals.filter((p) => p.status === 'refused').length,
            })}
          onClick={() => openRoute(ROUTES.review({ principal: `run:${run.id}` }))}
        />
        {/* Cells is the research run's own figure: the raster cells it filed, counted from its
            `fact` proposals. A skill that fills no cells has none, and "not collected" is what
            that reads as — never a zero nobody counted. */}
        <RailCell
          label={t('Cells')}
          value={filesCells ? <Fig>{cellsFiled}</Fig> : t('not collected')}
          tone={!filesCells ? 'unknown' : cellsFiled === 0 ? 'nil' : 'ink'}
          detail={!filesCells
            ? t('this skill fills no raster cell')
            : cellsFiled > 0
              ? t('raster cells filed as proposals · held in Review')
              : ended(run.status) ? t('it filed no cell')
                : t('cells arrive as it reads')}
        />
      </Rail>

      {/* ── 1 · the conversation ─────────────────────────────────────────── */}
      <Zone id="steps">
        <ZoneHead
          label={t('Steps')}
          count={steps.length ? <Fig>{steps.length}</Fig> : undefined}
          note={live
            ? t('appended live · every three seconds')
            : stepsTotal !== null && stepsTotal > steps.length
              ? t('{n} of {total} recorded', { n: steps.length, total: stepsTotal })
              : t('the account this run left of itself')}
        >
          <button
            type="button"
            className={`btn btn-sm btn-ghost${follow ? ' active' : ''}`}
            aria-pressed={follow}
            disabled={!steps.length}
            title={t('Keep the newest step in view as it arrives')}
            onClick={() => setFollow((v) => !v)}
          >
            {t('Follow')}
          </button>
        </ZoneHead>
        <Sheet clip>
          {stepsError ? (
            <p className="zone-empty">{t('The steps did not load: {error}', { error: stepsError })}</p>
          ) : stepsRecorded === false ? (
            <p className="zone-empty">
              {t('This server has recorded no steps for this run — the steps route did not answer. What the run called is still below in the ledger, and what it filed is still in Review.')}
            </p>
          ) : steps.length === 0 ? (
            <p className="zone-empty">
              {live
                ? t('No step yet. The loop writes one as it queues, starts, plans, reads a page, calls the model and proposes or drops a cell; they arrive here without a reload.')
                : t('This run recorded no step. It ran before the steps table existed, or it ended before it wrote one.')}
            </p>
          ) : (
            <>
              {steps.map((step) => (
                <StepRow
                  key={step.id}
                  step={step}
                  open={openStep === step.id}
                  onToggle={() => setOpenStep((k) => (k === step.id ? null : step.id))}
                />
              ))}
              <div ref={tail} />
            </>
          )}
        </Sheet>
      </Zone>

      {/* ── 2 · what it filed ────────────────────────────────────────────── */}
      <Zone id="proposals">
        <ZoneHead
          label={t('Proposals')}
          count={proposals.length ? <Fig>{proposals.length}</Fig> : undefined}
          note={t('nothing is on the record until an operator holds it')}
          route={proposals.length
            ? <button type="button" className="zone-route" onClick={() => openRoute(ROUTES.review({ principal: `run:${run.id}` }))}>{t('Review')} →</button>
            : undefined}
        />
        <Sheet clip>
          {proposals.length === 0 ? (
            <p className="zone-empty">
              {t('This run has filed nothing. A run proposes: a fact with its quote, a person with the line that names them, a draft. Each one waits in Review for a click.')}
            </p>
          ) : proposals.map((p) => (
            <div className="row" style={{ gridTemplateColumns: '110px 130px 1fr auto' }} key={p.id}>
              <span><Lnk href={href(ROUTES.review({ proposal: p.id }))}><Ref>{p.ref}</Ref></Lnk></span>
              <span className="d">{t(p.kind)}</span>
              <span>
                {p.status === 'refused' || p.status === 'amended'
                  ? null
                  : <><Att kind={p.status === 'held' ? 'held' : 'proposed'} />{' '}</>}
                <Tag tone={p.status === 'held' ? 'ok' : p.status === 'refused' ? 'broken' : 'ghost'}>{t(p.status)}</Tag>
              </span>
              <span className="d voice-figure">{fmtDateTime(p.created_at)}</span>
            </div>
          ))}
        </Sheet>
      </Zone>

      {/* ── 3 · the ledger ───────────────────────────────────────────────── */}
      <Zone id="ledger">
        <ZoneHead
          label={t('Ledger')}
          count={calls.length ? <Fig>{calls.length}</Fig> : undefined}
          note={t('every call this run made · refusals included')}
        />
        <Sheet clip>
          {calls.length === 0 ? (
            <p className="zone-empty">
              {t('This run called no tool. Only an agent that goes through the registry writes ledger rows.')}
            </p>
          ) : calls.map((c) => (
            <div className="row" style={{ gridTemplateColumns: '150px 90px minmax(0,1fr) 80px' }} key={c.id}>
              <span className={c.outcome === 'refused' || c.outcome === 'error' ? 'fv--broken' : undefined}>
                <Ref>{c.tool}</Ref>
              </span>
              <span className={c.outcome === 'ok' ? 'd' : 'fv--broken'}>{t(OUTCOME_WORD[c.outcome] ?? c.outcome)}</span>
              <span className="d" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.error ?? undefined}>
                {c.error ? c.error : c.input ? JSON.stringify(c.input) : t('no input recorded')}
              </span>
              <span className="d voice-figure" style={{ textAlign: 'right' }}>
                {c.latency_ms === null ? '—' : <Fig unit="ms">{c.latency_ms}</Fig>}
              </span>
            </div>
          ))}
        </Sheet>
      </Zone>

      {/* ── 4 · the report ───────────────────────────────────────────────── */}
      <Zone id="report">
        <ZoneHead
          label={t('Report')}
          note={detail?.transcript ? t('the agent’s final answer, as it wrote it') : t('the agent writes one when it ends')}
        />
        <Sheet pad>
          {detail?.transcript ? (
            // A long report scrolls in its own well rather than making the page a mile long —
            // it is the agent's own words, and reads in the record's voice.
            <div className="voice-record" style={{ whiteSpace: 'pre-wrap', maxHeight: 460, overflow: 'auto' }}>{detail.transcript}</div>
          ) : childLog?.length ? (
            <>
              <p className="zone-empty">{t('The child’s own output, while it runs. Not a report, and not a claim.')}</p>
              <pre className="mono wpre" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 320, overflow: 'auto' }}>
                {childLog.join('\n')}
              </pre>
            </>
          ) : (
            <p className="zone-empty">
              {live
                ? t('No report yet. The agent answers when it has finished; its steps above say what it is doing meanwhile.')
                : t('This run left no report. It ended before the agent answered; its steps and its ledger are the account.')}
            </p>
          )}
        </Sheet>
      </Zone>

      <ConfirmModal
        open={abortOpen}
        title={t('Stop {ref}?', { ref: run.ref })}
        confirmLabel={busy ? t('Saving…') : t('Abort')}
        danger
        onCancel={() => setAbortOpen(false)}
        onConfirm={() => { void doAbort(); }}
      >
        <p className="read">
          {t('It stops at its next step boundary. Everything it has already filed stays in Review, and everything it has already spent stays on the record — a stopped run is a shorter run, not an undone one.')}
        </p>
      </ConfirmModal>
    </PageShell>
  );
}

// ═══ one step ═══════════════════════════════════════════════════════════════

/**
 * One line of the conversation: when, what kind, what it says, what it cost. Only `drop` and
 * `refusal` colour their title — they are the rows that say a thing did not happen, and
 * colouring the rest would make a working run look like a failing one.
 */
function StepRow({ step, open, onToggle }: { step: RunStep; open: boolean; onToggle: () => void }) {
  const t = useT();
  const { fmtNum } = useLocale();
  const fig = (n: number): string => swissTicks(fmtNum(n));
  const prose = modelText(step);
  const structure = !prose && step.detail && Object.keys(step.detail).length > 0
    ? JSON.stringify(step.detail, null, 2)
    : null;
  const hasDetail = Boolean(prose || structure);
  const tokens = step.tokens_in !== null || step.tokens_out !== null;

  return (
    <div className="row" style={{ gridTemplateColumns: '78px 92px minmax(0,1fr) auto', alignItems: 'baseline' }}>
      <span className="d voice-figure">{stepTime(step.at)}</span>
      <span><Tag>{t(STEP_LABEL[step.kind] ?? step.kind)}</Tag></span>
      <span>
        <span className={BROKEN_KINDS.has(step.kind) ? 'fv--broken' : undefined}>{step.title}</span>
        {hasDetail && (
          <>
            {' '}
            <Lnk onClick={onToggle} title={t('What this step recorded, as it recorded it')}>
              {open ? t('hide') : t('detail')}
            </Lnk>
          </>
        )}
        {open && prose && (
          <pre className="voice-record wpre" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 320, overflow: 'auto' }}>
            {prose}
          </pre>
        )}
        {open && !prose && structure && (
          <pre className="mono wpre" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 320, overflow: 'auto' }}>
            {structure}
          </pre>
        )}
      </span>
      <span className="d voice-figure" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        {tokens ? <><Fig>{fig(step.tokens_in ?? 0)}</Fig> / <Fig>{fig(step.tokens_out ?? 0)}</Fig></> : ' '}
      </span>
    </div>
  );
}
